<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import { computed, ref } from 'vue'
import { getEvolutionPrice, getPetName } from '../data'
import type { PetType } from '@/types/pet'
import PetInfo from './PetInfo.vue'
import { formatNumberWithCommas } from '@/utils'
import { QuestionFilled } from '@element-plus/icons-vue'
import { socketEmitAsync } from '@/utils/web-socket'

const userStore = useUserStore()

const petList = computed(() => {
  return userStore.petList.filter((item) => {
    if (item.id === userStore.fightPetId) return false
    if (userStore.outsidePetIdList.includes(item.id)) return false
    if (item.quality !== 'legend') return false
    return true
  })
})

const petInfo = ref<PetType>()
function handleClear() {
  petInfo.value = undefined
}

const resPetInfo = ref<PetType>()
const resShow = ref(false)
async function handleFusion() {
  if (!petInfo.value) return ElMessage.warning('请放入魔物')
  const price = getEvolutionPrice(petInfo.value?.monsterId)
  if (userStore.gold < price) return ElMessage.warning('金币不足')
  if (userStore.consumables.antiquitySoul < 1) return ElMessage.warning('远古之魂不足')
  ElMessageBox.confirm(`<p class="affix">确定进行<span class="info">进化</span>吗？（该操作将重新随机魔物属性）</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      if (!petInfo.value) return
      const id = petInfo.value.id
      const res = await socketEmitAsync<PetType>({ type: 'petEvolution', data: { id } })

      const index = userStore.petList.findIndex((item) => item.id === id)
      userStore.petList[index] = res
      userStore.consumables.antiquitySoul--
      userStore.gold -= price

      resPetInfo.value = res
      resShow.value = true

      handleClear()
    })
    .catch(() => {})
}
</script>

<template>
  <div class="pet-evolution">
    <div class="bench border-wrap">
      <h4>
        实验台
        <div class="help-info">
          <el-popover placement="bottom" width="100%" trigger="click">
            <template #reference>
              <el-icon size="20"><QuestionFilled /></el-icon>
            </template>
            <div class="affix">
              <h4>进化说明</h4>
              <p>1. 使用<span class="antiquity">远古之魂</span>可对<span class="legend">传说</span>魔物进行<span class="info">进化</span>。</p>
              <p>2. 进化后的魔物品质将升级为<span class="antiquity">远古</span>，并重新随机数值（不会低于现有评分）。</p>
              <p>3. 魔物阶级越高，进化所需的<span class="gold">金币</span>越多。</p>
            </div>
          </el-popover>
        </div>
      </h4>
      <div class="pet-container">
        <el-button type="primary" plain @click="handleClear">
          <span :class="petInfo?.quality">{{ petInfo?.monsterId ? getPetName(petInfo?.monsterId) : '（未放入）' }}</span>
        </el-button>
      </div>
      <div class="res-tips">
        <p class="affix">
          <span>
            结果：
            <template v-if="petInfo">
              <span class="antiquity">{{ getPetName(petInfo?.monsterId) }}</span>
            </template>
            <template v-else>（请放入魔物）</template>
          </span>
        </p>
        <p>花费：{{ formatNumberWithCommas(getEvolutionPrice(petInfo?.monsterId)) }} 金币</p>
        <p style="text-indent: 3em">1 远古之魂</p>

        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleFusion">开始进化</el-button>
        </div>
      </div>
    </div>

    <div class="pet-list">
      <div class="left border-wrap">
        <h4>魔物列表</h4>
        <p style="font-size: 12px; text-align: right; margin: 0 5px 5px 0">
          <span style="margin-right: 5px">💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
          <span>🧬 {{ formatNumberWithCommas(userStore.consumables.antiquitySoul) }}</span>
        </p>
        <div class="item-btn-wrap btn-wrap" style="margin-top: 0">
          <el-row :gutter="10">
            <el-col :span="24" v-for="item of petList" :key="item.id">
              <CommonBtn size="small" @done="petInfo = item" :style="{ borderColor: petInfo?.id === item.id ? 'red' : '' }">
                <span :class="item.quality" v-html="getPetName(item.monsterId)"></span>
              </CommonBtn>
            </el-col>
          </el-row>
          <p class="affix" style="text-align: center" v-show="!petList.length">您的背包中不存在<span class="legend">传说</span>魔物</p>
        </div>
      </div>
      <div class="right border-wrap">
        <h4>魔物信息</h4>
        <template v-if="petInfo">
          <PetInfo :pet-info="petInfo" :show-action="false" />
        </template>
      </div>
    </div>

    <el-dialog v-model="resShow" width="100%" style="max-width: 500px">
      <div class="fusion-res">
        <h2 class="affix">
          <span class="grow">进化成功</span>
        </h2>
        <template v-if="resPetInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <CommonBtn size="small">
                <span :class="resPetInfo.quality">{{ getPetName(resPetInfo.monsterId) }}</span>
              </CommonBtn>
            </template>
            <PetInfo :pet-info="resPetInfo" :show-action="false" />
          </el-popover>
        </template>
      </div>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="resShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.pet-evolution {
  display: flex;
  flex-flow: column nowrap;
  .border-wrap {
    padding: 0 10px 10px 10px;
  }
  .bench {
    margin-bottom: 10px;
    position: relative;
    p {
      font-size: 14px;
      margin: 8px 0;
    }
    .pet-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      i {
        margin: 0 10px;
      }
      .el-button {
        flex: 1;
      }
    }
    .res-tips {
      p {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }
    .help-info {
      position: absolute;
      top: 10px;
      right: 30px;
    }
  }
  .pet-list {
    flex: 1;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    .left {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-flow: column nowrap;
      padding: 0;
      width: calc(50% - 5px);
    }
    .right {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-flow: column nowrap;
      width: calc(50% - 5px);
    }
  }
  .fusion-res,
  .pet-list {
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
  .btn-wrap {
    max-height: 100%;
    overflow: auto;
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .item-btn-wrap {
    & > div {
      margin-bottom: 10px;
    }
  }
}
</style>
